import { createContext, useLayoutEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { POST_LANGUAGE_OPTIONS } from '../types/post';

export type AppLanguage = (typeof POST_LANGUAGE_OPTIONS)[number]['value'];

export interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: TranslationKey) => string;
}

const STORAGE_KEY = 'iti-language';
const DEFAULT_LANGUAGE: AppLanguage = 'en';

export const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const VALID_LANGUAGES = new Set<AppLanguage>(POST_LANGUAGE_OPTIONS.map((option) => option.value));

type TranslationKey =
  | 'footer.help'
  | 'common.loading'
  | 'common.login'
  | 'common.logout'
  | 'common.language'
  | 'common.cancel'
  | 'header.users'
  | 'header.moderators'
  | 'header.statistics'
  | 'header.flaggedPosts'
  | 'header.flaggedComments'
  | 'header.deletedPosts'
  | 'header.deletedComments'
  | 'header.notifications'
  | 'notifications.markAllRead'
  | 'notifications.markAllReadTitle'
  | 'notifications.clearAll'
  | 'notifications.clearAllTitle'
  | 'notifications.viewAll'
  | 'notifications.viewAllTitle'
  | 'notifications.loading'
  | 'notifications.empty'
  | 'notifications.noMore'
  | 'notifications.unreadBadge'
  | 'notifications.refresh'
  | 'notifications.loadMore'
  | 'notifications.confirmClearAll'
  | 'notifications.error.load'
  | 'notifications.bell.aria'
  | 'notifications.bell.ariaWithUnread'
  | 'header.messages'
  | 'header.profile'
  | 'header.openMainMenu'
  | 'header.openMainMenuWithUnread'
  | 'header.closeMainMenu'
  | 'header.mainNavigation'
  | 'post.createTitle'
  | 'post.editTitle'
  | 'post.title'
  | 'post.titleOptional'
  | 'post.titlePlaceholder'
  | 'post.content'
  | 'post.contentPlaceholder'
  | 'post.language'
  | 'post.sightingType'
  | 'post.sightingTypeOptional'
  | 'post.sightingTypeRequired'
  | 'post.clear'
  | 'post.tagsOptional'
  | 'post.tags'
  | 'post.tagsHelper'
  | 'post.createButton'
  | 'post.updateButton'
  | 'post.createNewButton'
  | 'post.createSteckbrief'
  | 'post.browseSteckbriefe'
  | 'post.noneFound'
  | 'post.deleted'
  | 'post.by'
  | 'post.viewAllComments'
  | 'post.clickAddComment'
  | 'post.noCommentsYet'
  | 'post.backToPostList'
  | 'post.sightingTypeLabel'
  | 'post.locationMapRegion'
  | 'post.pairingTypeLabel'
  | 'post.pairingTypeHeading'
  | 'post.comment'
  | 'post.comments'
  | 'post.commentsHeading'
  | 'post.showDeletedComments'
  | 'post.loginToComment'
  | 'post.commentsLoginInvite'
  | 'post.loginRequiredNotice'
  | 'post.loginPopupBlocked'
  | 'post.reopenLoginTab'
  | 'post.loginNowButton'
  | 'post.loginWaitingNotice'
  | 'post.createRequiresAccountTitle'
  | 'post.createRequiresAccountBody'
  | 'post.createRequiresAccountRegister'
  | 'post.createAuthBackHome'
  | 'post.notFound'
  | 'pagination.showing'
  | 'pagination.to'
  | 'pagination.of'
  | 'pagination.posts'
  | 'pagination.first'
  | 'pagination.previous'
  | 'pagination.next'
  | 'pagination.last'
  | 'pagination.perPage'
  | 'theme.switchToLight'
  | 'theme.switchToDark'
  | 'theme.light'
  | 'theme.dark'
  | 'theme.lightDark'
  | 'comment.noneYet'
  | 'comment.addLink'
  | 'comment.confirmDelete'
  | 'comment.editorLabel'
  | 'comment.editorPlaceholder'
  | 'comment.submitNew'
  | 'comment.submitUpdate'
  | 'comment.validationContentRequired'
  | 'comment.validationContentMax'
  | 'home.crimeClubOffer'
  | 'home.heroTaglineLead'
  | 'home.heroTaglineSub'
  | 'home.heroHeadline'
  | 'home.heroBody1'
  | 'home.heroBrand'
  | 'home.feedAllHeading'
  | 'messages.input.placeholder'
  | 'messages.input.placeholderToUser'
  | 'messages.input.sendTitle'
  | 'messages.input.sendTitleEmpty'
  | 'messages.sidebar.title'
  | 'messages.sidebar.newTooltip'
  | 'messages.sidebar.newAria'
  | 'messages.chat.loading'
  | 'messages.chat.loadingSuffix'
  | 'messages.chat.backAria'
  | 'messages.startWithUser'
  | 'messages.empty.selectThread'
  | 'messages.empty.hintPlus'
  | 'messages.threadList.loading'
  | 'messages.threadList.empty'
  | 'messages.threadList.noPreview'
  | 'messages.list.loading'
  | 'messages.list.empty'
  | 'messages.list.loadOlder'
  | 'messages.list.statusRead'
  | 'messages.list.statusSent'
  | 'messages.modal.title'
  | 'messages.modal.description'
  | 'messages.modal.closeAria'
  | 'messages.search.placeholder'
  | 'messages.search.failed'
  | 'messages.search.noResults'
  | 'messages.search.activeLabel'
  | 'messages.delete.tooltip'
  | 'messages.delete.aria'
  | 'messages.delete.confirm'
  | 'header.adminModerators'
  | 'moderation.reasonRequired'
  | 'moderation.noReasonProvided'
  | 'moderation.softDelete'
  | 'moderation.restorePost'
  | 'moderation.restoreComment'
  | 'moderation.deletePermanently'
  | 'moderation.restore'
  | 'moderation.approve'
  | 'moderation.reject'
  | 'moderation.deleting'
  | 'moderation.restoring'
  | 'moderation.approving'
  | 'moderation.rejecting'
  | 'moderation.submitting'
  | 'moderation.softDeletePostTitle'
  | 'moderation.confirmSoftDeletePost'
  | 'moderation.reasonForDeletion'
  | 'moderation.reasonForDeletionPlaceholder'
  | 'moderation.restorePostTitle'
  | 'moderation.confirmRestorePost'
  | 'moderation.reasonForRestoration'
  | 'moderation.reasonForRestorationPlaceholder'
  | 'moderation.softDeleteCommentTitle'
  | 'moderation.confirmSoftDeleteComment'
  | 'moderation.restoreCommentTitle'
  | 'moderation.confirmRestoreComment'
  | 'moderation.errorDeletePost'
  | 'moderation.errorRestorePost'
  | 'moderation.errorDeleteComment'
  | 'moderation.errorRestoreComment'
  | 'moderation.flaggedPostsTitle'
  | 'moderation.flaggedPostsDescription'
  | 'moderation.flaggedPostsEmpty'
  | 'moderation.fetchFlaggedPostsFailed'
  | 'moderation.dateFlagged'
  | 'moderation.columnReason'
  | 'moderation.postTitleAndContent'
  | 'moderation.columnAuthor'
  | 'moderation.columnActions'
  | 'moderation.approveFlagTitle'
  | 'moderation.approveFlagPostExplain'
  | 'moderation.rejectFlagTitle'
  | 'moderation.rejectFlagPostExplain'
  | 'moderation.reasonOptional'
  | 'moderation.reasonApprovalPlaceholder'
  | 'moderation.reasonRejectionPlaceholder'
  | 'moderation.failedApproveFlag'
  | 'moderation.failedRejectFlag'
  | 'moderation.flaggedCommentsTitle'
  | 'moderation.flaggedCommentsDescription'
  | 'moderation.flaggedCommentsEmpty'
  | 'moderation.fetchFlaggedCommentsFailed'
  | 'moderation.columnCommentContent'
  | 'moderation.columnPost'
  | 'moderation.viewPost'
  | 'moderation.approveFlagCommentExplain'
  | 'moderation.rejectFlagCommentExplain'
  | 'moderation.deletedPostsTitle'
  | 'moderation.deletedPostsDescription'
  | 'moderation.deletedPostsEmpty'
  | 'moderation.columnDeletionReason'
  | 'moderation.permanentDeletePostTitle'
  | 'moderation.permanentDeletePostWarning'
  | 'moderation.permanentDeletePostConfirm'
  | 'moderation.failedPermanentDeletePost'
  | 'moderation.failedRestorePost'
  | 'moderation.deletedCommentsTitle'
  | 'moderation.deletedCommentsDescription'
  | 'moderation.deletedCommentsEmpty'
  | 'moderation.permanentDeleteCommentTitle'
  | 'moderation.permanentDeleteCommentWarning'
  | 'moderation.permanentDeleteCommentConfirm'
  | 'moderation.fetchDeletedCommentsFailed'
  | 'moderation.failedPermanentDeleteComment'
  | 'moderation.failedRestoreComment'
  | 'moderation.report'
  | 'moderation.flagPostAria'
  | 'moderation.flagCommentAria'
  | 'moderation.flagInappropriateTitle'
  | 'moderation.flagPostExplain'
  | 'moderation.flagCommentExplain'
  | 'moderation.flagReasonOptionalPlaceholder'
  | 'moderation.flagPostSubmit'
  | 'moderation.flagCommentSubmit'
  | 'moderation.failedFlagPost'
  | 'moderation.failedFlagComment'
  | 'moderation.loadingEditHistory'
  | 'moderation.editHistoryErrorPrefix'
  | 'moderation.editHistoryLoadFailed'
  | 'moderation.noEditHistory'
  | 'moderation.editHistoryTitle'
  | 'moderation.editHistoryReasonLabel'
  | 'moderation.editHistoryPrevious'
  | 'moderation.editHistoryNew'
  | 'moderation.dashboardTitle'
  | 'moderation.statDeletedPosts'
  | 'moderation.statDeletedComments'
  | 'moderation.statTotalActions'
  | 'moderation.dashboardHint'
  | 'admin.title'
  | 'admin.description'
  | 'admin.addSectionTitle'
  | 'admin.usernameLabel'
  | 'admin.usernamePlaceholder'
  | 'admin.addButton'
  | 'admin.currentModerators'
  | 'admin.emptyModerators'
  | 'admin.addedLabel'
  | 'admin.remove'
  | 'admin.removeTitle'
  | 'admin.removeConfirm'
  | 'admin.enterUsername'
  | 'admin.failedLoad'
  | 'admin.failedAdd'
  | 'admin.failedRemove'
  | 'stats.title'
  | 'stats.description'
  | 'stats.sectionUsers'
  | 'stats.sectionEngagement'
  | 'stats.sectionContent'
  | 'stats.sectionModeration'
  | 'stats.sectionTrends'
  | 'stats.registeredUsers'
  | 'stats.loginsToday'
  | 'stats.loginsMonth'
  | 'stats.loginsTotal'
  | 'stats.uniqueToday'
  | 'stats.uniqueMonth'
  | 'stats.uniqueTotal'
  | 'stats.avgSession'
  | 'stats.postsTotal'
  | 'stats.postsToday'
  | 'stats.postsMonth'
  | 'stats.commentsTotal'
  | 'stats.commentsToday'
  | 'stats.commentsMonth'
  | 'stats.deletedPosts'
  | 'stats.deletedComments'
  | 'stats.moderationActions'
  | 'stats.trendLogins'
  | 'stats.trendPosts'
  | 'stats.trendComments'
  | 'stats.trendDate'
  | 'stats.trendCount'
  | 'stats.failedLoad'
  | 'stats.utcNote'
  | 'stats.chartUsersLogins'
  | 'stats.chartEngagement'
  | 'stats.chartContent'
  | 'stats.chartLogins'
  | 'stats.chartUniqueUsers'
  | 'stats.chartAvgSession'
  | 'stats.chartPosts'
  | 'stats.chartComments'
  | 'post.actionComment'
  | 'post.actionContact'
  | 'post.actionShare'
  | 'post.actionReport'
  | 'post.actionDelete'
  | 'post.confirmDeleteTitle'
  | 'post.confirmDeleteText'
  | 'post.createNoticeTitle'
  | 'post.createNoticeText'
  | 'post.createNoticeCta'
  | 'notification.newComment.title'
  | 'notification.newComment.infix'
  | 'profile.title'
  | 'profile.accountInfo'
  | 'profile.username'
  | 'profile.email'
  | 'profile.fullName'
  | 'profile.firstName'
  | 'profile.lastName'
  | 'profile.emailVerified'
  | 'profile.roles'
  | 'profile.userId'
  | 'profile.yes'
  | 'profile.no'
  | 'profile.na'
  | 'profile.manageAccount'
  | 'profile.uploadPicture'
  | 'profile.changePicture'
  | 'profile.deletePicture'
  | 'profile.compressing'
  | 'profile.uploading'
  | 'profile.confirmDeletePicture'
  | 'profile.errorLoadPicture'
  | 'profile.errorNotImage'
  | 'profile.errorImageTooLarge'
  | 'profile.errorProcessPicture'
  | 'profile.errorDeletePicture'
  | 'cropper.title'
  | 'cropper.hint'
  | 'cropper.cancel'
  | 'cropper.apply'
  | 'profile.startChat'
  | 'profile.activity'
  | 'profile.postingActivity'
  | 'profile.totalPosts'
  | 'profile.recentPosts'
  | 'profile.showAllPosts'
  | 'profile.commentingActivity'
  | 'profile.totalComments'
  | 'profile.recentComments'
  | 'profile.showAllComments'
  | 'profile.viewPost'
  | 'profile.statistics'
  | 'profile.totalActivity'
  | 'profile.noActivity'
  | 'search.placeholder'
  | 'search.matches'
  | 'search.matchesOne'
  | 'search.noMatches'
  | 'search.resultsHeading'
  | 'search.showFilters'
  | 'search.hideFilters'
  | 'search.clearFilters'
  | 'search.filtersActive'
  | 'search.radiusDecrease'
  | 'search.radiusIncrease'
  | 'filter.sightingType'
  | 'filter.any'
  | 'filter.location'
  | 'filter.radius'
  | 'filter.dateRange'
  | 'filter.from'
  | 'filter.to'
  | 'static.missedConnections'
  | 'static.about'
  | 'static.symbol'
  | 'static.showSymbol'
  | 'static.signatureSong'
  | 'static.contact'
  | 'static.faq'
  | 'static.help'
  | 'static.communityRules'
  | 'static.terms'
  | 'static.privacy'
  | 'static.imprint'
  | 'header.infoMenu'
  | 'password.changeTitle'
  | 'password.newPassword'
  | 'password.confirmPassword'
  | 'password.changePlaceholder'
  | 'password.confirmPlaceholder'
  | 'password.changeButton'
  | 'password.changing'
  | 'password.success'
  | 'password.mismatch'
  | 'password.tooShort'
  | 'password.tooWeak'
  | 'password.veryWeak'
  | 'password.weak'
  | 'password.fair'
  | 'password.strong'
  | 'password.veryStrong'
  | 'password.requirements'
  | 'location.useCurrentLocation'
  | 'location.searchPlaceholder'
  | 'location.searching'
  | 'location.geoError'
  | 'location.geoNotSupported'
  | 'tag.inputPlaceholder'
  | 'dateTime.label'
  | 'dateTime.placeholder'
  | 'validation.titleRequired'
  | 'validation.titleMax'
  | 'validation.contentRequired'
  | 'validation.contentMax'
  | 'validation.locationRequired'
  | 'validation.dateTimeRequired'
  | 'share.title'
  | 'share.linkLabel'
  | 'share.copy'
  | 'share.copied'
  | 'share.generating'
  | 'share.error'
  | 'post.inspirationTitle'
  | 'password.ruleMinLength'
  | 'password.needsUppercase'
  | 'password.needsLowercase'
  | 'password.needsDigit'
  | 'password.needsSpecial';

const MESSAGES: Record<AppLanguage, Record<TranslationKey, string>> = {
  en: {
    'footer.help': 'Help',
    'common.loading': 'Loading...',
    'common.login': 'Login',
    'common.logout': 'Logout',
    'common.language': 'Language',
    'common.cancel': 'Cancel',
    'header.users': 'Users',
    'header.moderators': 'Moderators',
    'header.statistics': 'Statistics',
    'header.flaggedPosts': 'Flagged Posts',
    'header.flaggedComments': 'Flagged Comments',
    'header.deletedPosts': 'Deleted Posts',
    'header.deletedComments': 'Deleted Comments',
    'header.notifications': 'Notifications',
    'notifications.markAllRead': 'Mark all read',
    'notifications.markAllReadTitle': 'Mark all notifications as read',
    'notifications.clearAll': 'Clear all',
    'notifications.clearAllTitle': 'Delete all notifications',
    'notifications.viewAll': 'View all',
    'notifications.viewAllTitle': 'View all notifications',
    'notifications.loading': 'Loading notifications...',
    'notifications.empty': 'No notifications yet',
    'notifications.noMore': 'No more notifications',
    'notifications.unreadBadge': '{count} unread',
    'notifications.refresh': 'Refresh',
    'notifications.loadMore': 'Load more',
    'notifications.confirmClearAll':
      'Are you sure you want to delete all notifications? This action cannot be undone.',
    'notifications.error.load': 'Failed to load notifications',
    'notifications.bell.aria': 'Notifications',
    'notifications.bell.ariaWithUnread': 'Notifications ({count} unread)',
    'header.messages': 'Messages',
    'header.profile': 'Profile',
    'header.openMainMenu': 'Open main menu',
    'header.openMainMenuWithUnread': 'Open main menu (unread messages or notifications)',
    'header.closeMainMenu': 'Close main menu',
    'header.mainNavigation': 'Main menu',
    'post.createTitle': 'Create New Post',
    'post.editTitle': 'Edit Post',
    'post.title': 'Title',
    'post.titleOptional': 'Title (optional)',
    'post.titlePlaceholder': 'Enter a title…',
    'post.content': 'Content',
    'post.contentPlaceholder': 'Describe your encounter…',
    'share.title': 'Share Post',
    'share.linkLabel': 'Share Link',
    'share.copy': 'Copy',
    'share.copied': 'Copied!',
    'share.generating': 'Generating share link…',
    'share.error': 'Failed to generate share link',
    'post.inspirationTitle': 'Inspiration for your sighting',
    'post.language': 'Language',
    'post.sightingType': 'Post Type',
    'post.sightingTypeOptional': 'Sighting Type (optional)',
    'post.sightingTypeRequired': 'Please select a post type',
    'post.clear': 'Clear',
    'post.tagsOptional': 'Tags (optional)',
    'post.tags': 'Tags',
    'post.tagsHelper': 'Add tags to categorize your post. Press Enter or Space to add a tag.',
    'post.createButton': 'Create Post',
    'post.updateButton': 'Update Post',
    'post.createNewButton': 'Create New Post',
    'post.createSteckbrief': 'Create Wanted Poster',
    'post.browseSteckbriefe': 'Browse Wanted Posters',
    'post.noneFound': 'No posts found. Be the first to create one!',
    'post.deleted': 'Deleted',
    'post.by': 'by',
    'post.viewAllComments': 'View all comments',
    'post.clickAddComment': 'Comment here',
    'post.noCommentsYet': 'Comment here',
    'post.backToPostList': 'Back to Post list',
    'post.sightingTypeLabel': 'Sighting type:',
    'post.locationMapRegion': 'Map: encounter location',
    'post.pairingTypeLabel': 'Constellation:',
    'post.pairingTypeHeading': 'Who saw whom?',
    'post.comment': 'comment',
    'post.comments': 'comments',
    'post.commentsHeading': 'Comments',
    'post.showDeletedComments': 'Show deleted comments',
    'post.loginToComment': 'Please log in to leave a comment.',
    'post.commentsLoginInvite': 'Sign in to leave a comment.',
    'post.loginRequiredNotice': 'You need to be logged in to create a post. Your draft will be saved.',
    'post.loginPopupBlocked': 'Could not open the login tab. Please allow pop-ups for this site and try again.',
    'post.reopenLoginTab': 'Log in or Sign up',
    'post.loginNowButton': 'Log in now',
    'post.loginWaitingNotice': 'Waiting for you to log in… Your draft has been saved and will be posted automatically.',
    'post.createRequiresAccountTitle': 'Account required',
    'post.createRequiresAccountBody': 'To create a wanted poster you need to sign in or register.',
    'post.createRequiresAccountRegister': 'Register',
    'post.createAuthBackHome': 'Back to home',
    'post.notFound': 'Post not found',
    'pagination.showing': 'Showing',
    'pagination.to': 'to',
    'pagination.of': 'of',
    'pagination.posts': 'posts',
    'pagination.first': 'First',
    'pagination.previous': 'Previous',
    'pagination.next': 'Next',
    'pagination.last': 'Last',
    'pagination.perPage': 'Per page:',
    'theme.switchToLight': 'Switch to light theme',
    'theme.switchToDark': 'Switch to dark theme',
    'theme.light': 'Light',
    'theme.dark': 'Dark',
    'theme.lightDark': 'Light / Dark',
    'comment.noneYet': 'No comments yet.',
    'comment.addLink': 'Click here to add a comment',
    'comment.confirmDelete': 'Delete this comment? This cannot be undone.',
    'comment.editorLabel': 'Comment',
    'comment.editorPlaceholder': 'Enter your comment…',
    'comment.submitNew': 'Post comment',
    'comment.submitUpdate': 'Update comment',
    'comment.validationContentRequired': 'Please enter a comment.',
    'comment.validationContentMax': 'Comment must not exceed 5000 characters.',
    'home.crimeClubOffer': 'An offer of Crime Club!',
    'home.heroTaglineLead': 'Missed Connections',
    'home.heroTaglineSub': 'The platform for missed encounters.',
    'home.heroHeadline': 'Unknown — but unforgettable.',
    'home.heroBody1':
      'Create a profile for the person who caught your eye — anonymous, location-based, and safe. Or find out if someone is already looking for you. On the train, at the theatre, or after a special night: every clue counts.',
    'home.heroBrand': "I Think It's You",
    'home.feedAllHeading': 'All wanted posters',
    'messages.input.placeholder': 'Type your message...',
    'messages.input.placeholderToUser': 'Type a message to {username}...',
    'messages.input.sendTitle': 'Send message (Enter)',
    'messages.input.sendTitleEmpty': 'Type a message to send',
    'messages.sidebar.title': 'Conversations',
    'messages.sidebar.newTooltip': 'New conversation',
    'messages.sidebar.newAria': 'Start new conversation',
    'messages.chat.loading': 'Loading...',
    'messages.chat.loadingSuffix': ' (loading…)',
    'messages.chat.backAria': 'Back to conversations',
    'messages.startWithUser': 'Start the conversation with {username}',
    'messages.empty.selectThread': 'Select a conversation to start messaging',
    'messages.empty.hintPlus': 'Click the + button to start a new conversation',
    'messages.threadList.loading': 'Loading threads...',
    'messages.threadList.empty': 'No conversations yet',
    'messages.threadList.noPreview': 'No messages yet',
    'messages.list.loading': 'Loading messages...',
    'messages.list.empty': 'No messages yet. Start the conversation!',
    'messages.list.loadOlder': 'Load older messages',
    'messages.list.statusRead': 'Read',
    'messages.list.statusSent': 'Sent',
    'messages.modal.title': 'New Conversation',
    'messages.modal.description': 'Search for a user to start a new conversation',
    'messages.modal.closeAria': 'Close',
    'messages.search.placeholder': 'Search by username...',
    'messages.search.failed': 'Failed to search users',
    'messages.search.noResults': 'No users found matching "{query}"',
    'messages.search.activeLabel': 'Active',
    'messages.delete.tooltip': 'Delete conversation',
    'messages.delete.aria': 'Delete conversation',
    'messages.delete.confirm': 'Delete this conversation and all messages? This cannot be undone.',
    'header.adminModerators': 'Manage moderators',
    'moderation.reasonRequired': 'Reason is required',
    'moderation.noReasonProvided': 'No reason provided',
    'moderation.softDelete': 'Soft delete',
    'moderation.restorePost': 'Restore post',
    'moderation.restoreComment': 'Restore comment',
    'moderation.deletePermanently': 'Delete permanently',
    'moderation.restore': 'Restore',
    'moderation.approve': 'Approve',
    'moderation.reject': 'Reject',
    'moderation.deleting': 'Deleting...',
    'moderation.restoring': 'Restoring...',
    'moderation.approving': 'Approving…',
    'moderation.rejecting': 'Rejecting…',
    'moderation.submitting': 'Submitting...',
    'moderation.softDeletePostTitle': 'Soft delete post',
    'moderation.confirmSoftDeletePost': 'Are you sure you want to soft delete this post?',
    'moderation.reasonForDeletion': 'Reason for deletion',
    'moderation.reasonForDeletionPlaceholder': 'Enter reason for deletion...',
    'moderation.restorePostTitle': 'Restore post',
    'moderation.confirmRestorePost': 'Are you sure you want to restore this post?',
    'moderation.reasonForRestoration': 'Reason for restoration',
    'moderation.reasonForRestorationPlaceholder': 'Enter reason for restoration...',
    'moderation.softDeleteCommentTitle': 'Soft delete comment',
    'moderation.confirmSoftDeleteComment': 'Are you sure you want to soft delete this comment?',
    'moderation.restoreCommentTitle': 'Restore comment',
    'moderation.confirmRestoreComment': 'Are you sure you want to restore this comment?',
    'moderation.errorDeletePost': 'Failed to delete post',
    'moderation.errorRestorePost': 'Failed to restore post',
    'moderation.errorDeleteComment': 'Failed to delete comment',
    'moderation.errorRestoreComment': 'Failed to restore comment',
    'moderation.flaggedPostsTitle': 'Flagged posts',
    'moderation.flaggedPostsDescription':
      'Posts reported as inappropriate. Approve (uphold) or reject (dismiss) each flag. Only visible to moderators.',
    'moderation.flaggedPostsEmpty': 'No flagged posts.',
    'moderation.fetchFlaggedPostsFailed': 'Failed to fetch flagged posts',
    'moderation.dateFlagged': 'Date flagged',
    'moderation.columnReason': 'Reason',
    'moderation.postTitleAndContent': 'Post title & content',
    'moderation.columnAuthor': 'Author',
    'moderation.columnActions': 'Actions',
    'moderation.approveFlagTitle': 'Approve flag',
    'moderation.approveFlagPostExplain': 'Uphold the flag? The post will remain hidden.',
    'moderation.rejectFlagTitle': 'Reject flag',
    'moderation.rejectFlagPostExplain': 'Dismiss the flag? The post will become visible again.',
    'moderation.reasonOptional': 'Reason (optional)',
    'moderation.reasonApprovalPlaceholder': 'Reason for approval...',
    'moderation.reasonRejectionPlaceholder': 'Reason for rejection...',
    'moderation.failedApproveFlag': 'Failed to approve flag',
    'moderation.failedRejectFlag': 'Failed to reject flag',
    'moderation.flaggedCommentsTitle': 'Flagged comments',
    'moderation.flaggedCommentsDescription':
      'Comments reported as inappropriate. Approve (uphold) or reject (dismiss) each flag. Only visible to moderators.',
    'moderation.flaggedCommentsEmpty': 'No flagged comments.',
    'moderation.fetchFlaggedCommentsFailed': 'Failed to fetch flagged comments',
    'moderation.columnCommentContent': 'Comment content',
    'moderation.columnPost': 'Post',
    'moderation.viewPost': 'View post',
    'moderation.approveFlagCommentExplain': 'Uphold the flag? The comment will be hidden (soft-deleted).',
    'moderation.rejectFlagCommentExplain': 'Dismiss the flag? The comment will become visible again.',
    'moderation.deletedPostsTitle': 'Deleted posts',
    'moderation.deletedPostsDescription': 'View all soft-deleted posts. Only visible to moderators.',
    'moderation.deletedPostsEmpty': 'No deleted posts found.',
    'moderation.columnDeletionReason': 'Deletion reason',
    'moderation.permanentDeletePostTitle': 'Permanently delete post',
    'moderation.permanentDeletePostWarning':
      'Warning: This action cannot be undone. The post will be permanently removed from the database.',
    'moderation.permanentDeletePostConfirm': 'Are you sure you want to permanently delete this post?',
    'moderation.failedPermanentDeletePost': 'Failed to permanently delete post',
    'moderation.failedRestorePost': 'Failed to restore post',
    'moderation.deletedCommentsTitle': 'Deleted comments',
    'moderation.deletedCommentsDescription': 'View all soft-deleted comments. Only visible to moderators.',
    'moderation.deletedCommentsEmpty': 'No deleted comments found.',
    'moderation.permanentDeleteCommentTitle': 'Permanently delete comment',
    'moderation.permanentDeleteCommentWarning':
      'Warning: This action cannot be undone. The comment will be permanently removed from the database.',
    'moderation.permanentDeleteCommentConfirm': 'Are you sure you want to permanently delete this comment?',
    'moderation.fetchDeletedCommentsFailed': 'Failed to fetch deleted comments',
    'moderation.failedPermanentDeleteComment': 'Failed to permanently delete comment',
    'moderation.failedRestoreComment': 'Failed to restore comment',
    'moderation.report': 'Report',
    'moderation.flagPostAria': 'Flag post as inappropriate',
    'moderation.flagCommentAria': 'Flag comment as inappropriate',
    'moderation.flagInappropriateTitle': 'Flag as inappropriate',
    'moderation.flagPostExplain':
      'This post will be hidden from the public list until a moderator reviews it.',
    'moderation.flagCommentExplain': 'This comment will be hidden until a moderator reviews it.',
    'moderation.flagReasonOptionalPlaceholder': 'Optional reason for flagging...',
    'moderation.flagPostSubmit': 'Flag post',
    'moderation.flagCommentSubmit': 'Flag comment',
    'moderation.failedFlagPost': 'Failed to flag post',
    'moderation.failedFlagComment': 'Failed to flag comment',
    'moderation.loadingEditHistory': 'Loading edit history...',
    'moderation.editHistoryErrorPrefix': 'Error: ',
    'moderation.editHistoryLoadFailed': 'Failed to load edit history',
    'moderation.noEditHistory': 'No edit history available for this post.',
    'moderation.editHistoryTitle': 'Edit History',
    'moderation.editHistoryReasonLabel': 'Reason:',
    'moderation.editHistoryPrevious': 'Previous:',
    'moderation.editHistoryNew': 'New:',
    'moderation.dashboardTitle': 'Moderation dashboard',
    'moderation.statDeletedPosts': 'Deleted posts',
    'moderation.statDeletedComments': 'Deleted comments',
    'moderation.statTotalActions': 'Total actions',
    'moderation.dashboardHint':
      'Moderation controls are available on individual posts and comments. Use the moderation actions (delete/restore) to manage content.',
    'admin.title': 'Manage moderators',
    'admin.description':
      'Add or remove the MODERATOR role from users. Only users who have posted, commented, or messaged can be found by username.',
    'admin.addSectionTitle': 'Add moderator',
    'admin.usernameLabel': 'Username',
    'admin.usernamePlaceholder': 'e.g. johndoe',
    'admin.addButton': 'Add as moderator',
    'admin.currentModerators': 'Current moderators',
    'admin.emptyModerators': 'No moderators in the list yet.',
    'admin.addedLabel': 'Added',
    'admin.remove': 'Remove',
    'admin.removeTitle': 'Remove moderator',
    'admin.removeConfirm':
      'Remove MODERATOR role from this user? They will lose moderator capabilities.',
    'admin.enterUsername': 'Enter a username',
    'admin.failedLoad': 'Failed to load moderators',
    'admin.failedAdd': 'Failed to add moderator',
    'admin.failedRemove': 'Failed to remove moderator',
    'stats.title': 'Platform statistics',
    'stats.description':
      'Logins, time on site, posts, comments, and moderation metrics. Counts for today and this month use UTC.',
    'stats.sectionUsers': 'Users & logins',
    'stats.sectionEngagement': 'Engagement',
    'stats.sectionContent': 'Content',
    'stats.sectionModeration': 'Moderation',
    'stats.sectionTrends': 'Last 30 days (UTC)',
    'stats.registeredUsers': 'Registered users',
    'stats.loginsToday': 'Logins today',
    'stats.loginsMonth': 'Logins this month',
    'stats.loginsTotal': 'Logins (all time)',
    'stats.uniqueToday': 'Unique users today',
    'stats.uniqueMonth': 'Unique users this month',
    'stats.uniqueTotal': 'Unique users (ever logged in)',
    'stats.avgSession': 'Avg. session duration',
    'stats.postsTotal': 'Posts (active)',
    'stats.postsToday': 'Posts today',
    'stats.postsMonth': 'Posts this month',
    'stats.commentsTotal': 'Comments (active)',
    'stats.commentsToday': 'Comments today',
    'stats.commentsMonth': 'Comments this month',
    'stats.deletedPosts': 'Deleted posts',
    'stats.deletedComments': 'Deleted comments',
    'stats.moderationActions': 'Moderation actions',
    'stats.trendLogins': 'Daily logins',
    'stats.trendPosts': 'Daily posts',
    'stats.trendComments': 'Daily comments',
    'stats.trendDate': 'Date',
    'stats.trendCount': 'Count',
    'stats.failedLoad': 'Failed to load statistics',
    'stats.utcNote': 'Daily and monthly boundaries are UTC.',
    'stats.chartUsersLogins': 'Daily logins & unique users (last 30 days)',
    'stats.chartEngagement': 'Average session duration per day (last 30 days)',
    'stats.chartContent': 'Daily posts & comments (last 30 days)',
    'stats.chartLogins': 'Logins',
    'stats.chartUniqueUsers': 'Unique users',
    'stats.chartAvgSession': 'Avg. session',
    'stats.chartPosts': 'Posts',
    'stats.chartComments': 'Comments',
    'post.actionComment': 'Comment',
    'post.actionContact': 'Contact',
    'post.actionShare': 'Share',
    'post.actionReport': 'Report',
    'post.actionDelete': 'Delete',
    'post.confirmDeleteTitle': 'Delete Post',
    'post.confirmDeleteText': 'Are you sure you want to delete this post? This action cannot be undone.',
    'post.createNoticeTitle': 'Before you post',
    'post.createNoticeText': 'Please be respectful and do not share any personal data (full names, phone numbers, addresses). Posts that violate our guidelines will be removed.',
    'post.createNoticeCta': 'Understood – create wanted poster',
    'notification.newComment.title': 'New comment on your post',
    'notification.newComment.infix': 'commented on your post:',
    'profile.title': 'User Profile',
    'profile.accountInfo': 'Account Information',
    'profile.username': 'Username:',
    'profile.email': 'Email:',
    'profile.fullName': 'Full Name:',
    'profile.firstName': 'First Name:',
    'profile.lastName': 'Last Name:',
    'profile.emailVerified': 'Email Verified:',
    'profile.roles': 'Roles:',
    'profile.userId': 'User ID:',
    'profile.yes': 'Yes',
    'profile.no': 'No',
    'profile.na': 'N/A',
    'profile.manageAccount': 'Change username, email or password ↗',
    'profile.uploadPicture': 'Upload Picture',
    'profile.changePicture': 'Change Picture',
    'profile.deletePicture': 'Delete Picture',
    'profile.compressing': 'Compressing...',
    'profile.uploading': 'Uploading...',
    'profile.confirmDeletePicture': 'Are you sure you want to delete your profile picture?',
    'profile.errorLoadPicture': 'Failed to load profile picture',
    'profile.errorNotImage': 'Please select an image file',
    'profile.errorImageTooLarge': 'Image size must be less than 5MB',
    'profile.errorProcessPicture': 'Failed to process or upload profile picture',
    'profile.errorDeletePicture': 'Failed to delete profile picture',
    'cropper.title':  'Crop profile picture',
    'cropper.hint':   'Drag to move · Scroll or pinch to zoom',
    'cropper.cancel': 'Cancel',
    'cropper.apply':  'Apply',
    'profile.startChat': 'Start Chat',
    'profile.activity': 'Your Activity',
    'profile.postingActivity': 'Posting Activity',
    'profile.totalPosts': 'Total Posts:',
    'profile.recentPosts': 'Recent Posts',
    'profile.showAllPosts': 'Show All Posts ({count})',
    'profile.commentingActivity': 'Commenting Activity',
    'profile.totalComments': 'Total Comments:',
    'profile.recentComments': 'Recent Comments',
    'profile.showAllComments': 'Show All Comments ({count})',
    'profile.viewPost': 'View Post',
    'profile.statistics': 'Statistics',
    'profile.totalActivity': 'Total Activity:',
    'profile.noActivity': 'No activity data available.',
    'search.placeholder': 'Search posts, comments, tags…',
    'search.matches': '{matches} of {total} matches',
    'search.matchesOne': '{matches} of {total} match',
    'search.noMatches': 'No matches in {total} posts',
    'search.resultsHeading': 'Found wanted posters',
    'search.showFilters': 'Filters',
    'search.hideFilters': 'Filters',
    'search.clearFilters': 'Clear filters',
    'search.filtersActive': 'Filters active',
    'search.radiusDecrease': 'Decrease search radius',
    'search.radiusIncrease': 'Increase search radius',
    'filter.sightingType': 'Sighting type',
    'filter.any': 'Any',
    'filter.location': 'Location',
    'filter.radius': 'Radius (km)',
    'filter.dateRange': 'Date range',
    'filter.from': 'From',
    'filter.to': 'To',
    'static.missedConnections': 'Missed Connections',
    'static.about': 'About I Think It\'s You',
    'static.symbol': 'The Symbol',
    'static.showSymbol': 'Show Symbol',
    'static.signatureSong': 'Signature Song',
    'static.contact': 'Contact',
    'static.faq': 'FAQ',
    'static.help': 'Help',
    'static.communityRules': 'Community Rules',
    'static.terms': 'Terms of Service',
    'static.privacy': 'Privacy Policy',
    'static.imprint': 'Imprint',
    'header.infoMenu': 'Info menu',
    'password.changeTitle': 'Change Password',
    'password.newPassword': 'New Password',
    'password.confirmPassword': 'Confirm Password',
    'password.changePlaceholder': 'Enter new password…',
    'password.confirmPlaceholder': 'Repeat new password…',
    'password.changeButton': 'Change Password',
    'password.changing': 'Changing…',
    'password.success': 'Password changed successfully!',
    'password.mismatch': 'Passwords do not match',
    'password.tooShort': 'At least 8 characters required',
    'password.tooWeak': 'Password is too weak — choose a stronger one',
    'password.veryWeak': 'Very weak',
    'password.weak': 'Weak',
    'password.fair': 'Fair',
    'password.strong': 'Strong',
    'password.veryStrong': 'Very strong',
    'password.requirements': 'Min. 8 characters. Use uppercase, lowercase, numbers and special characters for a strong password.',
    'location.useCurrentLocation': 'Use Current Location',
    'location.searchPlaceholder': 'Search for a location…',
    'location.searching': 'Searching…',
    'location.geoError': 'Unable to get your location. Please enter coordinates manually or click on the map.',
    'location.geoNotSupported': 'Geolocation is not supported by your browser.',
    'tag.inputPlaceholder': 'Type tags and press Enter or Space…',
    'dateTime.label': 'Date & Time',
    'dateTime.placeholder': 'Select date and time',
    'validation.titleRequired': 'Title is required',
    'validation.titleMax': 'Title must not exceed 100 characters',
    'validation.contentRequired': 'Content is required',
    'validation.contentMax': 'Content must not exceed 1000 characters',
    'validation.locationRequired': 'Please pick a location',
    'validation.dateTimeRequired': 'Please pick a date and time',
    'password.ruleMinLength': 'At least 8 characters',
    'password.needsUppercase': 'At least one uppercase letter',
    'password.needsLowercase': 'At least one lowercase letter',
    'password.needsDigit': 'At least one digit',
    'password.needsSpecial': 'At least one special character',
  },
  de: {
    'footer.help': 'Hilfe',
    'common.loading': 'Wird geladen...',
    'common.login': 'Anmelden',
    'common.logout': 'Abmelden',
    'common.language': 'Sprache',
    'common.cancel': 'Abbrechen',
    'header.users': 'Benutzer',
    'header.moderators': 'Moderatoren',
    'header.statistics': 'Statistik',
    'header.flaggedPosts': 'Gemeldete Steckbriefe',
    'header.flaggedComments': 'Gemeldete Kommentare',
    'header.deletedPosts': 'Gelöschte Steckbriefe',
    'header.deletedComments': 'Gelöschte Kommentare',
    'header.notifications': 'Hinweise',
    'notifications.markAllRead': 'Alle als gelesen markieren',
    'notifications.markAllReadTitle': 'Alle Hinweise als gelesen markieren',
    'notifications.clearAll': 'Alle löschen',
    'notifications.clearAllTitle': 'Alle Hinweise löschen',
    'notifications.viewAll': 'Alle anzeigen',
    'notifications.viewAllTitle': 'Alle Hinweise anzeigen',
    'notifications.loading': 'Hinweise werden geladen...',
    'notifications.empty': 'Noch keine Hinweise',
    'notifications.noMore': 'Keine weiteren Hinweise',
    'notifications.unreadBadge': '{count} ungelesen',
    'notifications.refresh': 'Aktualisieren',
    'notifications.loadMore': 'Mehr laden',
    'notifications.confirmClearAll':
      'Alle Hinweise wirklich löschen? Dies kann nicht rückgängig gemacht werden.',
    'notifications.error.load': 'Hinweise konnten nicht geladen werden',
    'notifications.bell.aria': 'Hinweise',
    'notifications.bell.ariaWithUnread': 'Hinweise ({count} ungelesen)',
    'header.messages': 'Nachrichten',
    'header.profile': 'Profil',
    'header.openMainMenu': 'Hauptmenü öffnen',
    'header.openMainMenuWithUnread': 'Hauptmenü öffnen (ungelesene Nachrichten oder Hinweise)',
    'header.closeMainMenu': 'Hauptmenü schließen',
    'header.mainNavigation': 'Hauptmenü',
    'post.createTitle': 'Neuen Steckbrief erstellen',
    'post.editTitle': 'Steckbrief bearbeiten',
    'post.title': 'Titel',
    'post.titleOptional': 'Titel (optional)',
    'post.titlePlaceholder': 'Gib einen Titel ein…',
    'post.content': 'Inhalt',
    'post.contentPlaceholder': 'Beschreibe deine Begegnung…',
    'share.title': 'Steckbrief teilen',
    'share.linkLabel': 'Link zum Teilen',
    'share.copy': 'Kopieren',
    'share.copied': 'Kopiert!',
    'share.generating': 'Link wird erstellt…',
    'share.error': 'Link konnte nicht erstellt werden',
    'post.inspirationTitle': 'Inspiration für deine Sichtung',
    'post.language': 'Sprache',
    'post.sightingType': 'Steckbriefstyp',
    'post.sightingTypeOptional': 'Steckbriefstyp (optional)',
    'post.sightingTypeRequired': 'Bitte wähle einen Steckbriefstyp',
    'post.clear': 'Zurücksetzen',
    'post.tagsOptional': 'Schlagwörter (optional)',
    'post.tags': 'Schlagwörter',
    'post.tagsHelper': 'Füge Schlagwörter hinzu, um deinen Steckbrief zu kategorisieren. Enter oder Leertaste fügt ein Schlagwort hinzu.',
    'post.createButton': 'Steckbrief erstellen',
    'post.updateButton': 'Steckbrief aktualisieren',
    'post.createNewButton': 'Neuen Steckbrief erstellen',
    'post.createSteckbrief': 'Steckbrief erstellen',
    'post.browseSteckbriefe': 'Steckbriefe suchen',
    'post.noneFound': 'Keine Steckbriefe gefunden. Sei der Erste und erstelle einen!',
    'post.deleted': 'Gelöscht',
    'post.by': 'von',
    'post.viewAllComments': 'Alle Kommentare anzeigen',
    'post.clickAddComment': 'Hier kommentieren',
    'post.noCommentsYet': 'Hier kommentieren',
    'post.backToPostList': 'Zurück zur Steckbriefsliste',
    'post.sightingTypeLabel': 'Steckbriefstyp:',
    'post.locationMapRegion': 'Ort der Begegnung',
    'post.pairingTypeLabel': 'Konstellation:',
    'post.pairingTypeHeading': 'Wer sah wen?',
    'post.comment': 'Kommentar',
    'post.comments': 'Kommentare',
    'post.commentsHeading': 'Kommentare',
    'post.showDeletedComments': 'Gelöschte Kommentare anzeigen',
    'post.loginToComment': 'Bitte melde dich an, um einen Kommentar zu hinterlassen.',
    'post.commentsLoginInvite': 'Melde Dich an, um einen Kommentar zu schreiben.',
    'post.loginRequiredNotice': 'Du musst angemeldet sein, um einen Steckbrief zu erstellen. Dein Entwurf wird gespeichert.',
    'post.loginPopupBlocked': 'Der Login-Tab konnte nicht geöffnet werden. Bitte erlaube Pop-ups für diese Seite und versuche es erneut.',
    'post.reopenLoginTab': 'Einloggen oder Registrieren',
    'post.loginNowButton': 'Jetzt einloggen',
    'post.loginWaitingNotice': 'Warte auf deine Anmeldung… Dein Entwurf wurde gespeichert und wird automatisch gepostet.',
    'post.createRequiresAccountTitle': 'Konto erforderlich',
    'post.createRequiresAccountBody': 'Um einen Steckbrief zu erstellen, musst Du Dich anmelden oder registrieren.',
    'post.createRequiresAccountRegister': 'Registrieren',
    'post.createAuthBackHome': 'Zurück zur Startseite',
    'post.notFound': 'Steckbrief nicht gefunden',
    'pagination.showing': 'Zeige',
    'pagination.to': 'bis',
    'pagination.of': 'von',
    'pagination.posts': 'Steckbriefen',
    'pagination.first': 'Erste',
    'pagination.previous': 'Zurück',
    'pagination.next': 'Weiter',
    'pagination.last': 'Letzte',
    'pagination.perPage': 'Pro Seite:',
    'theme.switchToLight': 'Zum hellen Theme wechseln',
    'theme.switchToDark': 'Zum dunklen Theme wechseln',
    'theme.light': 'Hell',
    'theme.dark': 'Dunkel',
    'theme.lightDark': 'Hell / Dunkel',
    'comment.noneYet': 'Noch keine Kommentare.',
    'comment.addLink': 'Hier klicken, um einen Kommentar hinzuzufügen',
    'comment.confirmDelete': 'Diesen Kommentar wirklich löschen? Das kann nicht rückgängig gemacht werden.',
    'comment.editorLabel': 'Kommentar',
    'comment.editorPlaceholder': 'Kommentar hier eingeben …',
    'comment.submitNew': 'Kommentar absenden',
    'comment.submitUpdate': 'Kommentar aktualisieren',
    'comment.validationContentRequired': 'Bitte gib einen Kommentar ein.',
    'comment.validationContentMax': 'Kommentare sind auf 5000 Zeichen begrenzt.',
    'home.crimeClubOffer': 'Ein Angebot von Crime Club!',
    'home.heroTaglineLead': 'Missed Connections',
    'home.heroTaglineSub': 'Die Plattform für verpasste Begegnungen',
    'home.heroHeadline': 'Unbekannt – aber unvergessen.',
    'home.heroBody1':
      'Erstelle einen Steckbrief der Person, die Dir aufgefallen ist – anonym, ortsbasiert und sicher. Oder finde heraus, ob jemand bereits nach Dir sucht. Ob im Zug, im Theater oder nach einem besonderen Abend: Jede Spur zählt. :|:',
    'home.heroBrand': "I Think It's You",
    'home.feedAllHeading': 'Alle Steckbriefe',
    'messages.input.placeholder': 'Schreibe deine Nachricht...',
    'messages.input.placeholderToUser': 'Schreibe eine Nachricht an {username}...',
    'messages.input.sendTitle': 'Nachricht senden (Eingabetaste)',
    'messages.input.sendTitleEmpty': 'Tippe eine Nachricht zum Senden',
    'messages.sidebar.title': 'Unterhaltungen',
    'messages.sidebar.newTooltip': 'Neue Unterhaltung',
    'messages.sidebar.newAria': 'Neue Unterhaltung starten',
    'messages.chat.loading': 'Wird geladen...',
    'messages.chat.loadingSuffix': ' (lädt…)',
    'messages.chat.backAria': 'Zurück zu Unterhaltungen',
    'messages.startWithUser': 'Beginne die Unterhaltung mit {username}',
    'messages.empty.selectThread': 'Wähle eine Unterhaltung, um zu schreiben',
    'messages.empty.hintPlus': 'Klicke auf +, um eine neue Unterhaltung zu starten',
    'messages.threadList.loading': 'Unterhaltungen werden geladen...',
    'messages.threadList.empty': 'Noch keine Unterhaltungen',
    'messages.threadList.noPreview': 'Noch keine Nachrichten',
    'messages.list.loading': 'Nachrichten werden geladen...',
    'messages.list.empty': 'Noch keine Nachrichten. Schreib den ersten!',
    'messages.list.loadOlder': 'Ältere Nachrichten laden',
    'messages.list.statusRead': 'Gelesen',
    'messages.list.statusSent': 'Gesendet',
    'messages.modal.title': 'Neue Unterhaltung',
    'messages.modal.description': 'Suche einen Benutzer, um eine neue Unterhaltung zu starten',
    'messages.modal.closeAria': 'Schließen',
    'messages.search.placeholder': 'Benutzername suchen...',
    'messages.search.failed': 'Benutzersuche fehlgeschlagen',
    'messages.search.noResults': 'Keine Benutzer gefunden für "{query}"',
    'messages.search.activeLabel': 'Aktiv',
    'messages.delete.tooltip': 'Unterhaltung löschen',
    'messages.delete.aria': 'Unterhaltung löschen',
    'messages.delete.confirm': 'Diese Unterhaltung und alle Nachrichten löschen? Dies kann nicht rückgängig gemacht werden.',
    'header.adminModerators': 'Moderatoren verwalten',
    'moderation.reasonRequired': 'Grund ist erforderlich',
    'moderation.noReasonProvided': 'Kein Grund angegeben',
    'moderation.softDelete': 'Vorläufig löschen',
    'moderation.restorePost': 'Steckbrief wiederherstellen',
    'moderation.restoreComment': 'Kommentar wiederherstellen',
    'moderation.deletePermanently': 'Endgültig löschen',
    'moderation.restore': 'Wiederherstellen',
    'moderation.approve': 'Bestätigen',
    'moderation.reject': 'Ablehnen',
    'moderation.deleting': 'Wird gelöscht...',
    'moderation.restoring': 'Wird wiederhergestellt...',
    'moderation.approving': 'Wird bestätigt...',
    'moderation.rejecting': 'Wird abgelehnt...',
    'moderation.submitting': 'Wird gesendet...',
    'moderation.softDeletePostTitle': 'Steckbrief vorläufig löschen',
    'moderation.confirmSoftDeletePost': 'Diesen Steckbrief wirklich vorläufig löschen?',
    'moderation.reasonForDeletion': 'Grund für die Löschung',
    'moderation.reasonForDeletionPlaceholder': 'Grund für die Löschung eingeben...',
    'moderation.restorePostTitle': 'Steckbrief wiederherstellen',
    'moderation.confirmRestorePost': 'Diesen Steckbrief wirklich wiederherstellen?',
    'moderation.reasonForRestoration': 'Grund für die Wiederherstellung',
    'moderation.reasonForRestorationPlaceholder': 'Grund für die Wiederherstellung eingeben...',
    'moderation.softDeleteCommentTitle': 'Kommentar vorläufig löschen',
    'moderation.confirmSoftDeleteComment': 'Diesen Kommentar wirklich vorläufig löschen?',
    'moderation.restoreCommentTitle': 'Kommentar wiederherstellen',
    'moderation.confirmRestoreComment': 'Diesen Kommentar wirklich wiederherstellen?',
    'moderation.errorDeletePost': 'Steckbrief konnte nicht gelöscht werden',
    'moderation.errorRestorePost': 'Steckbrief konnte nicht wiederhergestellt werden',
    'moderation.errorDeleteComment': 'Kommentar konnte nicht gelöscht werden',
    'moderation.errorRestoreComment': 'Kommentar konnte nicht wiederhergestellt werden',
    'moderation.flaggedPostsTitle': 'Gemeldete Steckbriefe',
    'moderation.flaggedPostsDescription':
      'Als unangemessen gemeldete Steckbriefe. Jede Meldung bestätigen (aufrechterhalten) oder ablehnen (zurückweisen). Nur für Moderatoren sichtbar.',
    'moderation.flaggedPostsEmpty': 'Keine gemeldeten Steckbriefe.',
    'moderation.fetchFlaggedPostsFailed': 'Gemeldete Steckbriefe konnten nicht geladen werden',
    'moderation.dateFlagged': 'Meldedatum',
    'moderation.columnReason': 'Grund',
    'moderation.postTitleAndContent': 'Titel und Inhalt',
    'moderation.columnAuthor': 'Autor',
    'moderation.columnActions': 'Aktionen',
    'moderation.approveFlagTitle': 'Meldung bestätigen',
    'moderation.approveFlagPostExplain': 'Meldung aufrechterhalten? Der Steckbrief bleibt ausgeblendet.',
    'moderation.rejectFlagTitle': 'Meldung ablehnen',
    'moderation.rejectFlagPostExplain': 'Meldung zurückweisen? Der Steckbrief wird wieder sichtbar.',
    'moderation.reasonOptional': 'Grund (optional)',
    'moderation.reasonApprovalPlaceholder': 'Grund für die Bestätigung...',
    'moderation.reasonRejectionPlaceholder': 'Grund für die Ablehnung...',
    'moderation.failedApproveFlag': 'Meldung konnte nicht bestätigt werden',
    'moderation.failedRejectFlag': 'Meldung konnte nicht abgelehnt werden',
    'moderation.flaggedCommentsTitle': 'Gemeldete Kommentare',
    'moderation.flaggedCommentsDescription':
      'Als unangemessen gemeldete Kommentare. Jede Meldung bestätigen (aufrechterhalten) oder ablehnen (zurückweisen). Nur für Moderatoren sichtbar.',
    'moderation.flaggedCommentsEmpty': 'Keine gemeldeten Kommentare.',
    'moderation.fetchFlaggedCommentsFailed': 'Gemeldete Kommentare konnten nicht geladen werden',
    'moderation.columnCommentContent': 'Kommentarinhalt',
    'moderation.columnPost': 'Steckbrief',
    'moderation.viewPost': 'Steckbrief ansehen',
    'moderation.approveFlagCommentExplain':
      'Meldung aufrechterhalten? Der Kommentar wird ausgeblendet (vorläufig gelöscht).',
    'moderation.rejectFlagCommentExplain': 'Meldung zurückweisen? Der Kommentar wird wieder sichtbar.',
    'moderation.deletedPostsTitle': 'Gelöschte Steckbriefe',
    'moderation.deletedPostsDescription': 'Alle vorläufig gelöschten Steckbriefe. Nur für Moderatoren sichtbar.',
    'moderation.deletedPostsEmpty': 'Keine gelöschten Steckbriefe gefunden.',
    'moderation.columnDeletionReason': 'Löschgrund',
    'moderation.permanentDeletePostTitle': 'Steckbrief endgültig löschen',
    'moderation.permanentDeletePostWarning':
      'Achtung: Diese Aktion kann nicht rückgängig gemacht werden. Der Steckbrief wird dauerhaft aus der Datenbank entfernt.',
    'moderation.permanentDeletePostConfirm': 'Diesen Steckbrief wirklich endgültig löschen?',
    'moderation.failedPermanentDeletePost': 'Steckbrief konnte nicht endgültig gelöscht werden',
    'moderation.failedRestorePost': 'Steckbrief konnte nicht wiederhergestellt werden',
    'moderation.deletedCommentsTitle': 'Gelöschte Kommentare',
    'moderation.deletedCommentsDescription':
      'Alle vorläufig gelöschten Kommentare. Nur für Moderatoren sichtbar.',
    'moderation.deletedCommentsEmpty': 'Keine gelöschten Kommentare gefunden.',
    'moderation.permanentDeleteCommentTitle': 'Kommentar endgültig löschen',
    'moderation.permanentDeleteCommentWarning':
      'Achtung: Diese Aktion kann nicht rückgängig gemacht werden. Der Kommentar wird dauerhaft aus der Datenbank entfernt.',
    'moderation.permanentDeleteCommentConfirm': 'Diesen Kommentar wirklich endgültig löschen?',
    'moderation.fetchDeletedCommentsFailed': 'Gelöschte Kommentare konnten nicht geladen werden',
    'moderation.failedPermanentDeleteComment': 'Kommentar konnte nicht endgültig gelöscht werden',
    'moderation.failedRestoreComment': 'Kommentar konnte nicht wiederhergestellt werden',
    'moderation.report': 'Melden',
    'moderation.flagPostAria': 'Steckbrief als unangemessen melden',
    'moderation.flagCommentAria': 'Kommentar als unangemessen melden',
    'moderation.flagInappropriateTitle': 'Als unangemessen melden',
    'moderation.flagPostExplain':
      'Dieser Steckbrief wird in der öffentlichen Liste ausgeblendet, bis ein Moderator ihn geprüft hat.',
    'moderation.flagCommentExplain':
      'Dieser Kommentar wird ausgeblendet, bis ein Moderator ihn geprüft hat.',
    'moderation.flagReasonOptionalPlaceholder': 'Optionaler Grund für die Meldung...',
    'moderation.flagPostSubmit': 'Steckbrief melden',
    'moderation.flagCommentSubmit': 'Kommentar melden',
    'moderation.failedFlagPost': 'Steckbrief konnte nicht gemeldet werden',
    'moderation.failedFlagComment': 'Kommentar konnte nicht gemeldet werden',
    'moderation.loadingEditHistory': 'Bearbeitungsverlauf wird geladen...',
    'moderation.editHistoryErrorPrefix': 'Fehler: ',
    'moderation.editHistoryLoadFailed': 'Bearbeitungsverlauf konnte nicht geladen werden',
    'moderation.noEditHistory': 'Für diesen Steckbrief liegt kein Bearbeitungsverlauf vor.',
    'moderation.editHistoryTitle': 'Bearbeitungsverlauf',
    'moderation.editHistoryReasonLabel': 'Grund:',
    'moderation.editHistoryPrevious': 'Vorher:',
    'moderation.editHistoryNew': 'Neu:',
    'moderation.dashboardTitle': 'Moderationsbereich',
    'moderation.statDeletedPosts': 'Gelöschte Steckbriefe',
    'moderation.statDeletedComments': 'Gelöschte Kommentare',
    'moderation.statTotalActions': 'Aktionen gesamt',
    'moderation.dashboardHint':
      'Moderationsfunktionen findest du bei einzelnen Steckbriefen und Kommentaren. Nutze die Aktionen Löschen/Wiederherstellen, um Inhalte zu verwalten.',
    'admin.title': 'Moderatoren verwalten',
    'admin.description':
      'MODERATOR-Rolle bei Benutzern hinzufügen oder entfernen. Über den Benutzernamen sind nur Personen auffindbar, die bereits Steckbriefe, Kommentare oder Nachrichten geschrieben haben.',
    'admin.addSectionTitle': 'Moderator hinzufügen',
    'admin.usernameLabel': 'Benutzername',
    'admin.usernamePlaceholder': 'z. B. maxmustermann',
    'admin.addButton': 'Als Moderator hinzufügen',
    'admin.currentModerators': 'Aktuelle Moderatoren',
    'admin.emptyModerators': 'Noch keine Moderatoren in der Liste.',
    'admin.addedLabel': 'Hinzugefügt',
    'admin.remove': 'Entfernen',
    'admin.removeTitle': 'Moderator entfernen',
    'admin.removeConfirm':
      'MODERATOR-Rolle bei diesem Benutzer entfernen? Er verliert damit Moderationsrechte.',
    'admin.enterUsername': 'Benutzername eingeben',
    'admin.failedLoad': 'Moderatoren konnten nicht geladen werden',
    'admin.failedAdd': 'Moderator konnte nicht hinzugefügt werden',
    'admin.failedRemove': 'Moderator konnte nicht entfernt werden',
    'stats.title': 'Plattform-Statistik',
    'stats.description':
      'Anmeldungen, Verweildauer, Steckbriefe, Kommentare und Moderation. Tages- und Monatszahlen in UTC.',
    'stats.sectionUsers': 'Nutzer & Anmeldungen',
    'stats.sectionEngagement': 'Nutzung',
    'stats.sectionContent': 'Inhalte',
    'stats.sectionModeration': 'Moderation',
    'stats.sectionTrends': 'Letzte 30 Tage (UTC)',
    'stats.registeredUsers': 'Registrierte Nutzer',
    'stats.loginsToday': 'Anmeldungen heute',
    'stats.loginsMonth': 'Anmeldungen diesen Monat',
    'stats.loginsTotal': 'Anmeldungen (gesamt)',
    'stats.uniqueToday': 'Eindeutige Nutzer heute',
    'stats.uniqueMonth': 'Eindeutige Nutzer diesen Monat',
    'stats.uniqueTotal': 'Eindeutige Nutzer (jemals angemeldet)',
    'stats.avgSession': 'Ø Sitzungsdauer',
    'stats.postsTotal': 'Steckbriefe (aktiv)',
    'stats.postsToday': 'Steckbriefe heute',
    'stats.postsMonth': 'Steckbriefe diesen Monat',
    'stats.commentsTotal': 'Kommentare (aktiv)',
    'stats.commentsToday': 'Kommentare heute',
    'stats.commentsMonth': 'Kommentare diesen Monat',
    'stats.deletedPosts': 'Gelöschte Steckbriefe',
    'stats.deletedComments': 'Gelöschte Kommentare',
    'stats.moderationActions': 'Moderationsaktionen',
    'stats.trendLogins': 'Anmeldungen pro Tag',
    'stats.trendPosts': 'Steckbriefe pro Tag',
    'stats.trendComments': 'Kommentare pro Tag',
    'stats.trendDate': 'Datum',
    'stats.trendCount': 'Anzahl',
    'stats.failedLoad': 'Statistik konnte nicht geladen werden',
    'stats.utcNote': 'Tages- und Monatsgrenzen sind UTC.',
    'stats.chartUsersLogins': 'Anmeldungen & eindeutige Nutzer pro Tag (30 Tage)',
    'stats.chartEngagement': 'Ø Sitzungsdauer pro Tag (30 Tage)',
    'stats.chartContent': 'Steckbriefe & Kommentare pro Tag (30 Tage)',
    'stats.chartLogins': 'Anmeldungen',
    'stats.chartUniqueUsers': 'Eindeutige Nutzer',
    'stats.chartAvgSession': 'Ø Sitzung',
    'stats.chartPosts': 'Steckbriefe',
    'stats.chartComments': 'Kommentare',
    'post.actionComment': 'Kommentieren',
    'post.actionContact': 'Kontaktieren',
    'post.actionShare': 'Teilen',
    'post.actionReport': 'Melden',
    'post.actionDelete': 'Löschen',
    'post.confirmDeleteTitle': 'Steckbrief löschen',
    'post.confirmDeleteText': 'Möchtest du diesen Steckbrief wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'post.createNoticeTitle': 'Bevor du postest',
    'post.createNoticeText': 'Bitte sei respektvoll und teile keine persönlichen Daten (volle Namen, Telefonnummern, Adressen). Steckbriefe, die gegen unsere Richtlinien verstoßen, werden entfernt.',
    'post.createNoticeCta': 'Verstanden – Steckbrief erstellen',
    'notification.newComment.title': 'Neuer Kommentar zu deinem Steckbrief',
    'notification.newComment.infix': 'hat deinen Steckbrief kommentiert:',
    'profile.title': 'Benutzerprofil',
    'profile.accountInfo': 'Kontoinformationen',
    'profile.username': 'Benutzername:',
    'profile.email': 'E-Mail:',
    'profile.fullName': 'Vollständiger Name:',
    'profile.firstName': 'Vorname:',
    'profile.lastName': 'Nachname:',
    'profile.emailVerified': 'E-Mail bestätigt:',
    'profile.roles': 'Rollen:',
    'profile.userId': 'Benutzer-ID:',
    'profile.yes': 'Ja',
    'profile.no': 'Nein',
    'profile.na': 'N/A',
    'profile.manageAccount': 'Benutzername, E-Mail oder Passwort ändern ↗',
    'profile.uploadPicture': 'Bild hochladen',
    'profile.changePicture': 'Bild ändern',
    'profile.deletePicture': 'Bild löschen',
    'profile.compressing': 'Wird komprimiert...',
    'profile.uploading': 'Wird hochgeladen...',
    'profile.confirmDeletePicture': 'Profilbild wirklich löschen?',
    'profile.errorLoadPicture': 'Profilbild konnte nicht geladen werden',
    'profile.errorNotImage': 'Bitte wähle eine Bilddatei aus',
    'profile.errorImageTooLarge': 'Die Bilddatei muss kleiner als 5 MB sein',
    'profile.errorProcessPicture': 'Profilbild konnte nicht verarbeitet oder hochgeladen werden',
    'profile.errorDeletePicture': 'Profilbild konnte nicht gelöscht werden',
    'cropper.title':  'Profilbild zuschneiden',
    'cropper.hint':   'Ziehen zum Verschieben · Scrollen oder Pinchen zum Zoomen',
    'cropper.cancel': 'Abbrechen',
    'cropper.apply':  'Übernehmen',
    'profile.startChat': 'Chat starten',
    'profile.activity': 'Deine Aktivität',
    'profile.postingActivity': 'Steckbriefsaktivität',
    'profile.totalPosts': 'Steckbriefe gesamt:',
    'profile.recentPosts': 'Aktuelle Steckbriefe',
    'profile.showAllPosts': 'Alle Steckbriefe anzeigen ({count})',
    'profile.commentingActivity': 'Kommentaraktivität',
    'profile.totalComments': 'Kommentare gesamt:',
    'profile.recentComments': 'Aktuelle Kommentare',
    'profile.showAllComments': 'Alle Kommentare anzeigen ({count})',
    'profile.viewPost': 'Steckbrief ansehen',
    'profile.statistics': 'Statistiken',
    'profile.totalActivity': 'Aktivitäten gesamt:',
    'profile.noActivity': 'Keine Aktivitätsdaten verfügbar.',
    'search.placeholder': 'Steckbriefe, Kommentare durchsuchen',
    'search.matches': '{matches} von {total} Treffern',
    'search.matchesOne': '{matches} von {total} Treffer',
    'search.noMatches': 'Keine Treffer in {total} Steckbriefen',
    'search.resultsHeading': 'Gefundene Steckbriefe',
    'search.showFilters': 'Filter',
    'search.hideFilters': 'Filter',
    'search.clearFilters': 'Filter zurücksetzen',
    'search.filtersActive': 'Filter aktiv',
    'search.radiusDecrease': 'Suchradius verkleinern',
    'search.radiusIncrease': 'Suchradius vergrößern',
    'filter.sightingType': 'Steckbriefstyp',
    'filter.any': 'Alle',
    'filter.location': 'Ort',
    'filter.radius': 'Radius (km)',
    'filter.dateRange': 'Zeitraum',
    'filter.from': 'Von',
    'filter.to': 'Bis',
    'static.missedConnections': 'Missed Connections',
    'static.about': 'Über I Think It\'s You',
    'static.symbol': 'Das Symbol',
    'static.showSymbol': 'Symbol zeigen',
    'static.signatureSong': 'Signature Song',
    'static.contact': 'Kontakt',
    'static.faq': 'FAQ',
    'static.help': 'Hilfe',
    'static.communityRules': 'Community-Regeln',
    'static.terms': 'Nutzungsbedingungen',
    'static.privacy': 'Datenschutzerklärung',
    'static.imprint': 'Impressum',
    'header.infoMenu': 'Info-Menü',
    'password.changeTitle': 'Passwort ändern',
    'password.newPassword': 'Neues Passwort',
    'password.confirmPassword': 'Passwort bestätigen',
    'password.changePlaceholder': 'Neues Passwort eingeben…',
    'password.confirmPlaceholder': 'Neues Passwort wiederholen…',
    'password.changeButton': 'Passwort ändern',
    'password.changing': 'Wird geändert…',
    'password.success': 'Passwort erfolgreich geändert!',
    'password.mismatch': 'Passwörter stimmen nicht überein',
    'password.tooShort': 'Mindestens 8 Zeichen erforderlich',
    'password.tooWeak': 'Passwort ist zu schwach — wähle ein stärkeres',
    'password.veryWeak': 'Sehr schwach',
    'password.weak': 'Schwach',
    'password.fair': 'Mittel',
    'password.strong': 'Stark',
    'password.veryStrong': 'Sehr stark',
    'password.requirements': 'Min. 8 Zeichen. Verwende Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen für ein starkes Passwort.',
    'location.useCurrentLocation': 'Aktuellen Standort verwenden',
    'location.searchPlaceholder': 'Ort suchen…',
    'location.searching': 'Suche läuft…',
    'location.geoError': 'Standort konnte nicht ermittelt werden. Bitte gib Koordinaten manuell ein oder klicke auf die Karte.',
    'location.geoNotSupported': 'Standortbestimmung wird von deinem Browser nicht unterstützt.',
    'tag.inputPlaceholder': 'Schlagwörter eingeben, Enter oder Leertaste zum Hinzufügen…',
    'dateTime.label': 'Datum & Uhrzeit',
    'dateTime.placeholder': 'Datum und Uhrzeit wählen',
    'validation.titleRequired': 'Titel ist erforderlich',
    'validation.titleMax': 'Titel darf maximal 100 Zeichen lang sein',
    'validation.contentRequired': 'Inhalt ist erforderlich',
    'validation.contentMax': 'Inhalt darf maximal 1000 Zeichen lang sein',
    'validation.locationRequired': 'Bitte einen Ort auswählen',
    'validation.dateTimeRequired': 'Bitte Datum und Uhrzeit angeben',
    'password.ruleMinLength': 'Mindestens 8 Zeichen',
    'password.needsUppercase': 'Mindestens ein Großbuchstabe',
    'password.needsLowercase': 'Mindestens ein Kleinbuchstabe',
    'password.needsDigit': 'Mindestens eine Ziffer',
    'password.needsSpecial': 'Mindestens ein Sonderzeichen',
  },
};

function hasStoredLanguagePreference(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const saved = localStorage.getItem(STORAGE_KEY);
  return !!(saved && VALID_LANGUAGES.has(saved as AppLanguage));
}

function getInitialLanguage(): AppLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && VALID_LANGUAGES.has(saved as AppLanguage)) {
    return saved as AppLanguage;
  }

  const browserLanguages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  const prefersGerman = browserLanguages.some((lang) => lang?.toLowerCase().startsWith('de'));
  if (prefersGerman && VALID_LANGUAGES.has('de')) {
    return 'de';
  }

  return DEFAULT_LANGUAGE;
}

export const LanguageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<AppLanguage>(getInitialLanguage);
  const [hasUserPreference, setHasUserPreference] = useState<boolean>(hasStoredLanguagePreference);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('lang', language);
    if (hasUserPreference) {
      localStorage.setItem(STORAGE_KEY, language);
    }
  }, [language, hasUserPreference]);

  const setLanguage = (nextLanguage: AppLanguage) => {
    if (nextLanguage === language) {
      return;
    }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', nextLanguage);
    }
    setHasUserPreference(true);
    setLanguageState(nextLanguage);
  };

  const t = (key: TranslationKey): string => {
    return MESSAGES[language][key] ?? MESSAGES.en[key] ?? key;
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
